import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, Edit2, Check, Copy } from 'lucide-react';
import { DeviceTemplateProps, TraitStyleConfig, AttributeStyleConfig } from '../../types';

// Import dynamic trait/attribute renderers
import { DynamicTrait, DynamicAttribute, getTraitStyleId, getAttributeStyleId } from '../../DynamicTraitRenderer';

// DeviceIcon import
import DeviceIcon from '../../../deviceIcons';

/**
 * Light Template 01 - Full Features
 * Complete light control with brightness and color temperature
 */
export const Template01_Full: React.FC<DeviceTemplateProps> = ({
  device,
  isExecutingCommand,
  onTraitControl,
  onClose,
  onNicknameSave,
  isMerged,
  endpointCount,
  onToggleMerge,
  config
}) => {
  // Get styles from config
  const traitStyles = config?.traitStyles as TraitStyleConfig | undefined;
  const attributeStyles = config?.attributeStyles as AttributeStyleConfig | undefined;
  const smallIconStyle = config?.smallIconStyle || config?.iconStyle;
  const bigIconStyle = config?.bigIconStyle || config?.iconStyle;
  const hiddenComponents = config?.hiddenComponents || [];
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(device.nickname || device.deviceName);
  const blockSyncUntilRef = useRef<number>(0);
  const isSavingRef = useRef(false);

  // Sync tempName when device prop changes, but skip if we just saved
  useEffect(() => {
    if (!isEditingName && Date.now() > blockSyncUntilRef.current) {
      setTempName(device.nickname || device.deviceName);
    }
  }, [device.nickname, device.deviceName, isEditingName]);

  const handleSaveName = async () => {
    if (onNicknameSave && tempName.trim()) {
      isSavingRef.current = true;
      const savedName = tempName.trim();
      blockSyncUntilRef.current = Date.now() + 3000;
      await onNicknameSave(device.deviceId, savedName);
      setIsEditingName(false);
      isSavingRef.current = false;
    }
  };

  const handleBlur = () => {
    if (!isSavingRef.current) {
      setIsEditingName(false);
    }
  };

  const traits = device.traits || [];
  const attributes = device.attributes || {};
  
  // Color temperature range
  const colorTempRange = attributes.colorTemperatureRange || { temperatureMinK: 2700, temperatureMaxK: 6500 };

  const handleOnOff = () => {
    onTraitControl(device, 'OnOff', device.isOn ? 'off' : 'on');
  };

  const handleBrightnessChange = (value: number) => {
    onTraitControl(device, 'Brightness', 'setBrightness', { brightness: value });
  };

  const handleColorTempChange = (value: number) => {
    onTraitControl(device, 'ColorSetting', 'setColorTemperature', { color: { temperature: value } });
  };

  // Handle backdrop click - close modal or cancel editing
  const handleBackdropClick = useCallback(() => {
    if (isEditingName) {
      setIsEditingName(false);
      setTempName(device.nickname || device.deviceName);
    } else {
      onClose();
    }
  }, [isEditingName, device.nickname, device.deviceName, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" onClick={handleBackdropClick}>
      <div className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-sm max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType="light" 
              isOn={device.isOn} 
              size={20} 
              styleId={smallIconStyle}
              className="flex-shrink-0"
            />
            {isEditingName ? (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <input
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1 text-sm font-bold border border-slate-600 rounded bg-slate-800/80 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveName();
                    }
                    if (e.key === 'Escape') setIsEditingName(false);
                  }}
                  onBlur={handleBlur}
                />
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSaveName}
                  className="p-1 text-green-500 hover:text-green-400 flex-shrink-0"
                  title="儲存"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setIsEditingName(false)}
                  className="p-1 text-slate-400 hover:text-slate-300 flex-shrink-0"
                  title="取消"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-base font-bold text-white truncate">{device.nickname || device.deviceName}</h2>
                {onNicknameSave && (
                  <button 
                    onClick={() => {
                      setTempName(device.nickname || device.deviceName);
                      setIsEditingName(true);
                    }}
                    className="p-1 hover:bg-slate-700/50 rounded transition-colors flex-shrink-0"
                  >
                    <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Dynamic StatusIndicator */}
            <DynamicAttribute
              attributeName="StatusIndicator"
              styleId={getAttributeStyleId('StatusIndicator', attributeStyles)}
              componentProps={{
                online: device.status === 'online',
                label: device.status === 'online' ? 'ONLINE' : 'OFFLINE'
              }}
            />
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Light Visual - uses dynamic DeviceIcon */}
          {!hiddenComponents.includes('BigIcon') && (
            <div className="flex justify-center">
              <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
                device.isOn 
                  ? 'bg-gradient-to-br from-yellow-300 to-amber-400 shadow-lg shadow-yellow-500/50' 
                  : 'bg-slate-700'
              }`}>
                <DeviceIcon 
                  deviceType="light" 
                  isOn={device.isOn} 
                  size={64}
                  styleId={bigIconStyle}
                  className={device.isOn ? 'text-white' : 'text-slate-500'}
                />
              </div>
            </div>
          )}

          {/* Trait Controls - rendered in order based on config.traitOrder */}
          {(() => {
            // Get ordered traits from config, fallback to default order
            const defaultOrder = ['OnOff', 'Brightness', 'ColorSetting'];
            const traitOrder = config?.traitOrder || defaultOrder;
            
            // Filter to only include traits that this device has
            const orderedTraits = traitOrder.filter(t => traits.includes(t));
            // Add any traits not in the order (in case new traits were added)
            const remainingTraits = defaultOrder.filter(t => traits.includes(t) && !orderedTraits.includes(t));
            const finalOrder = [...orderedTraits, ...remainingTraits];
            
            return finalOrder.map(traitName => {
              // Check if component is hidden
              if (hiddenComponents.includes(traitName)) {
                return null;
              }
              
              switch (traitName) {
                case 'OnOff':
                  return (
                    <DynamicTrait
                      key="OnOff"
                      traitName="OnOff"
                      styleId={getTraitStyleId('OnOff', traitStyles)}
                      componentProps={{
                        isOn: device.isOn ?? false,
                        onToggle: handleOnOff,
                        disabled: isExecutingCommand,
                        loading: isExecutingCommand,
                        label: "電源"
                      }}
                    />
                  );
                case 'Brightness':
                  return (
                    <DynamicTrait
                      key="Brightness"
                      traitName="Brightness"
                      styleId={getTraitStyleId('Brightness', traitStyles)}
                      componentProps={{
                        value: device.brightness ?? 100,
                        min: 1,
                        max: 100,
                        onChange: handleBrightnessChange,
                        onChangeEnd: handleBrightnessChange,
                        disabled: isExecutingCommand || !device.isOn,
                        loading: isExecutingCommand,
                        label: "亮度"
                      }}
                    />
                  );
                case 'ColorSetting':
                  if (device.colorTemp === undefined) return null;
                  return (
                    <DynamicTrait
                      key="ColorSetting"
                      traitName="ColorSetting"
                      styleId={getTraitStyleId('ColorSetting', traitStyles)}
                      componentProps={{
                        value: device.colorTemp ?? 4000,
                        onChange: handleColorTempChange,
                        disabled: isExecutingCommand || !device.isOn,
                        min: colorTempRange.temperatureMinK,
                        max: colorTempRange.temperatureMaxK,
                        label: "色溫"
                      }}
                    />
                  );
                default:
                  return null;
              }
            });
          })()}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-800/50 bg-slate-900/30 space-y-2">
          {/* Multi-endpoint device merge toggle - only for multi_channel devices */}
          {device.isMultiChannel && endpointCount && endpointCount > 1 && onToggleMerge && (
            <div className="flex items-center justify-between py-1 px-2 bg-slate-700/30 rounded-lg">
              <span className="text-xs text-slate-400">
                多端點設備 ({endpointCount} 個)
              </span>
              <button
                onClick={() => onToggleMerge(device.baseSN)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  isMerged 
                    ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' 
                    : 'bg-slate-600/50 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {isMerged ? '顯示為個別方塊' : '顯示為單一方塊'}
              </button>
            </div>
          )}
          
          {/* Location & SN with copy */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{device.location || device.groupName || ''}</span>
            <div className="flex items-center gap-1">
              <span className="font-mono">{device.deviceId || device.baseSN}</span>
              <button
                onClick={() => navigator.clipboard.writeText(device.deviceId || device.baseSN)}
                className="text-slate-400 hover:text-white transition-colors"
                title="複製設備 SN"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template01_Full;
